import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, normalizeClass as _normalizeClass, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "container-fluid" }
const _hoisted_2 = { class: "row starter-main mt-5 pt-5" }
const _hoisted_3 = { class: "d-flex justify-content-end align-items-center gap-3 mt-3 mb-4" }
const _hoisted_4 = { class: "d-flex align-items-center gap-2" }
const _hoisted_5 = ["value"]
const _hoisted_6 = { class: "pagination-mini" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["disabled", "onClick"]
const _hoisted_9 = ["disabled"]

import { ref, computed, onMounted, watch } from "vue";
import { useCrud } from "@/composables/commen/useCrud";
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm from "@/components/commen/modal/BaseForm.vue";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import { usePermissions } from "@/composables/usePermissions";
import apiService from "@/services/apiService";
import { toast } from "vue3-toastify";

const API_RESOURCE = "/common/workspace-descriptions";
const FILTER_RESOURCE = "/common/workspace-descriptions-filter";
const category = "lane";
const pageTitle = "Lane Suggestion";
const title = "Lane Suggestions List";


export default /*@__PURE__*/_defineComponent({
  __name: 'LaneSuggessions',
  setup(__props) {

const { canCreate } = usePermissions();

const crud = useCrud(API_RESOURCE);
const isSubmitting = ref(false);
const isUploading = ref(false);

const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const currentPage = ref(1);

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;
  if (!total || total <= 4) return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  const pages: (number | string)[] = [];
  pages.push(1);
  if (current > 4) pages.push("...");
  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);
  if (current < total - 3) pages.push("...");
  pages.push(total);
  return pages;
});
 

const tableConfig = ref({
  columns: [
    { name: "Image", field: "featured_image", width: "100px" },
    { name: "Title", field: "title", sortable: true, width: "200px" },
    { name: "Description", field: "description", sortable: true, width: "auto" },
  ],
  data: [] as any[],
  actions: [true],
  loading: crud.isLoading,
  table_width: "100%",
});

// Custom fetch to handle the filter endpoint
const fetchFilteredData = async (params = { page: 1, limit: 10 }) => {
  crud.isLoading.value = true;
  try {
    const res = await apiService.get(FILTER_RESOURCE, {
      ...params,
      description_category: category,
    });
    // The filter endpoint response structure might differ, ensure it maps to what CommonTable2 expects
    if (res && res.data) {
       // standardizeResponse in apiService usually handles this, 
       // but useCrud's data is what we want to sync with
       crud.data.value = res.data;
       crud.pagination.value = res.pagination || {};
    } else if (Array.isArray(res)) {
       crud.data.value = res;
    }
  } catch (error) {
    console.error("Error fetching filtered lane suggestions:", error);
  } finally {
    crud.isLoading.value = false;
  }
};

watch(crud.data, (newVal) => {
  tableConfig.value.data = newVal.map(item => ({
    ...item,
    featured_image: item.file?.url // Map file.url to featured_image for CommonTable2 display
  }));
});

watch(crud.pagination, (newVal: any) => {
  if (!newVal) return;
  tablePagination.value.current_page = newVal.current_page || 1;
  tablePagination.value.total_pages = newVal.total_pages || 1;
  tablePagination.value.total = newVal.total || 0;
  tablePagination.value.has_prev = tablePagination.value.current_page > 1;
  tablePagination.value.has_next = tablePagination.value.current_page < tablePagination.value.total_pages;
}, { deep: true });

const changeRows = () => {
  currentPage.value = 1;
  fetchFilteredData({ page: 1, limit: tablePagination.value.per_page });
};

const handlePageChange = (page: number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  currentPage.value = page;
  fetchFilteredData({ page, limit: tablePagination.value.per_page });
};

const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);

const newItemData = ref<any>({ title: "", description: "", image: null, url: "", name: "" });
const editingItemData = ref<any>({ _id: "", title: "", description: "", image: null, url: "", name: "" });

const formFields = [
  { key: "title", label: "Title", type: "text", required: true, placeholder: "Enter title" },
  { key: "description", label: "Description", type: "textarea", required: true, placeholder: "Enter description" },
  { key: "image", label: "Image", type: "file", required: false, accept: "image/*" },
];

const formFieldsWithReadonly = computed(() => formFields.map(field => ({
  ...field,
  readonly: isViewMode.value
})));

const isCreateFormValid = computed(() => !newItemData.value.title?.trim() || !newItemData.value.description?.trim() || isUploading.value);
const isEditFormValid = computed(() => !editingItemData.value.title?.trim() || !editingItemData.value.description?.trim() || isUploading.value);

const handleEdit = (row: any) => {
  isViewMode.value = false;
  editingItemData.value = { 
    ...row, 
    image: row.file?.url || "",
    url: row.file?.url || "",
    name: row.file?.name || ""
  };
  isEditModalOpen.value = true;
};

const handleView = (row: any) => {
  isViewMode.value = true;
  editingItemData.value = { 
    ...row,
    image: row.file?.url || ""
  };
  isEditModalOpen.value = true;
};

const handleDelete = async (id: string) => {
  if (confirm("Are you sure you want to delete this lane suggestion?")) {
    await crud.deleteItem(id);
    fetchFilteredData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
  }
};

const uploadImage = async (file: File) => {
  isUploading.value = true;
  try {
    // Passing "upload/file" without leading slash to ensure it uses the baseURL properly
    const res = await apiService.uploadFile("upload/file-common", file);
    if (res && res.url) {
      toast.success("Image uploaded successfully");
      return { url: res.url, name: res.name || file.name };
    }
    toast.error("Image upload returned invalid data");
    return null;
  } catch (error) {
    console.error("Upload error:", error);
    toast.error("Image upload failed");
    return null;
  } finally {
    isUploading.value = false;
  }
};

// Watchers for immediate upload
watch(() => newItemData.value.image, async (newFile) => {
  if (newFile instanceof File) {
    const imageData = await uploadImage(newFile);
    if (imageData) {
      newItemData.value.url = imageData.url;
      newItemData.value.name = imageData.name;
    }
  }
});

watch(() => editingItemData.value.image, async (newFile) => {
  if (newFile instanceof File) {
    const imageData = await uploadImage(newFile);
    if (imageData) {
      editingItemData.value.url = imageData.url;
      editingItemData.value.name = imageData.name;
    }
  }
});

const handleSaveCreate = async () => {
  if (isCreateFormValid.value) return;
  isSubmitting.value = true;
  try {
    const payload = {
      title: newItemData.value.title,
      description: newItemData.value.description,
      description_category: category,
      file:{
          name: newItemData.value.name,
          url: newItemData.value.url,
      }
    };

    const result = await crud.createItem(payload);
    if (result) {
      closeCreateModal();
      fetchFilteredData({ page: 1, limit: tablePagination.value.per_page });
    }
  } catch (error) {
    console.error("Error creating lane suggestion:", error);
  } finally {
    isSubmitting.value = false;
  }
};

const handleSaveEdit = async () => {
  if (isEditFormValid.value) return;
  const id = editingItemData.value._id;
  if (!id) return;
  isSubmitting.value = true;
  try {
    const payload = {
      title: editingItemData.value.title,
      description: editingItemData.value.description,
      file:{
          name: editingItemData.value.name,
          url: editingItemData.value.url,
      }
    };

    const result = await crud.updateItem(id, payload);
    if (result) {
      closeEditModal();
      fetchFilteredData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
    }
  } catch (error) {
    console.error("Error updating lane suggestion:", error);
  } finally {
    isSubmitting.value = false;
  }
};

const closeCreateModal = () => {
  isCreateModalOpen.value = false;
  newItemData.value = { title: "", description: "", image: null, url: "", name: "" };
};

const closeEditModal = () => {
  isEditModalOpen.value = false;
  isViewMode.value = false;
  editingItemData.value = { _id: "", title: "", description: "", image: null, url: "", name: "" };
};


onMounted(() => {
  fetchFilteredData({ page: 1, limit: tablePagination.value.per_page });
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(TableTitle, {
          title: title,
          pageTitle: pageTitle,
          icon: "fas fa-road",
          showAction: true,
          actionLabel: "Create New",
          modal: "createNew",
          disabled: !_unref(canCreate)('data')
        }, null, 8, ["disabled"]),
        _createVNode(CommonTable2, {
          tableObj: tableConfig.value,
          onEdit: handleEdit,
          onDelete: handleDelete,
          onView: handleView,
          permissionKey: "data"
        }, null, 8, ["tableObj"])
      ])
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _cache[5] || (_cache[5] = _createElementVNode("span", { class: "text-muted" }, "Rows per page:", -1)),
        _withDirectives(_createElementVNode("select", {
          class: "form-select form-select-sm rows-select",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((tablePagination.value.per_page) = $event)),
          onChange: changeRows
        }, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList([5, 10, 20, 50, 100], (n) => {
            return _createElementVNode("option", {
              key: n,
              value: n
            }, _toDisplayString(n), 9, _hoisted_5)
          }), 64))
        ], 544), [
          [_vModelSelect, tablePagination.value.per_page]
        ])
      ]),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("button", {
          class: "page-btn prev_btn",
          disabled: !tablePagination.value.has_prev,
          onClick: _cache[1] || (_cache[1] = ($event: any) => (handlePageChange(tablePagination.value.current_page - 1)))
        }, " « ", 8, _hoisted_7),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visiblePages.value, (p, index) => {
          return (_openBlock(), _createElementBlock("button", {
            key: index,
            class: _normalizeClass(["page-btn", {
          'page-active': p === tablePagination.value.current_page,
          'disabled-dots': p === '...',
        }]),
            disabled: p === '...',
            onClick: ($event: any) => (p !== '...' && typeof p === 'number' && handlePageChange(p))
          }, _toDisplayString(p), 11, _hoisted_8))
        }), 128)),
        _createElementVNode("button", {
          class: "page-btn next_btn",
          disabled: !tablePagination.value.has_next,
          onClick: _cache[2] || (_cache[2] = ($event: any) => (handlePageChange(tablePagination.value.current_page + 1)))
        }, " » ", 8, _hoisted_9)
      ])
    ]),
    _createVNode(BaseModal, {
      id: "createNew",
      title: `Create ${pageTitle}`,
      visible: isCreateModalOpen.value,
      onClose: closeCreateModal,
      onSave: handleSaveCreate,
      "save-button-disabled": isCreateFormValid.value
    }, {
      body: _withCtx(() => [
        _createVNode(BaseForm, {
          fields: formFields,
          modelValue: newItemData.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newItemData).value = $event)),
          onSubmit: handleSaveCreate,
          ref: "createFormRef"
        }, null, 8, ["modelValue"])
      ]),
      _: 1
    }, 8, ["title", "visible", "save-button-disabled"]),
    _createVNode(BaseModal, {
      id: "editModal",
      title: `${isViewMode.value ? 'View' : 'Edit'} ${pageTitle}`,
      visible: isEditModalOpen.value,
      onClose: closeEditModal,
      onSave: handleSaveEdit,
      "show-save-button": !isViewMode.value,
      "save-button-disabled": isEditFormValid.value,
      loading: isSubmitting.value
    }, {
      body: _withCtx(() => [
        _createVNode(BaseForm, {
          fields: formFieldsWithReadonly.value,
          modelValue: editingItemData.value,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((editingItemData).value = $event)),
          onSubmit: handleSaveEdit,
          ref: "editFormRef"
        }, null, 8, ["fields", "modelValue"])
      ]),
      _: 1
    }, 8, ["title", "visible", "show-save-button", "save-button-disabled", "loading"])
  ], 64))
}
}

})